'use client';

import { useCallback, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { ArrowRight, Star, Gift, Sparkles, Sparkle } from 'lucide-react';
import { clsx } from 'clsx';
import tinycolor from 'tinycolor2';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import {
  card3dWrapper,
  card3dFloat,
  card3dScene,
  card3dShadowDeep,
  card3dShadowMid,
  card3dBody,
  card3dClickable,
  card3dBodyEnroll,
  card3dTitleEnroll,
  pointsRowEnroll,
  shineCircle1,
  shineCircle2,
  sparkleIcon,
  reflectionStrip,
  card3dTitle,
  punchRow3d,
  punchSlot3d,
  punchFilled3d,
  punchEmpty3d,
  punchProjected3d,
  giftSlot3d,
  giftSlotActive3d,
  compactRow3d,
  compactTrack3d,
  compactFill3d,
  compactCounter3d,
  pointsArea3d,
  pointsRow3d,
  pointsNum3d,
  pointsNumReward3d,
  pointsUnit3d,
  pointsUnitReward3d,
  enrollSubtitle,
  enrollPointsHint,
  enrollCta,
  enrollCtaIcon,
  ctaBgVar,
  ctaBgHoverVar,
  ctaTextVar,
} from './loyalty-card-3d.css';

type EnrollmentInfo = {
  subtitle: string;
  ctaLabel: string;
  onEnroll: () => void;
  pointsHint?: string;
};

type LoyaltyCard3DProps = {
  programName: string;
  rewardsAvailable?: number;
  pointsBalance?: number;
  accentColor?: string;
  isItemBased?: boolean;
  earningThreshold?: number;
  progress?: number;
  onClick?: () => void;
  enrollment?: EnrollmentInfo;
  rewardLabel?: string;
};

export const LoyaltyCard3D = observer(
  ({
    programName,
    rewardsAvailable = 0,
    pointsBalance = 0,
    accentColor,
    isItemBased = false,
    earningThreshold = 10,
    progress = 0,
    onClick,
    enrollment,
    rewardLabel,
  }: LoyaltyCard3DProps) => {
    const sceneRef = useRef<HTMLDivElement>(null);

    const brandColor = accentColor ?? '#34AC68';
    const brandDark = useMemo(() => tinycolor(brandColor).darken(20).toString(), [brandColor]);
    const brandDarker = useMemo(() => tinycolor(brandColor).darken(35).toString(), [brandColor]);
    const brandMid = useMemo(() => tinycolor(brandColor).darken(12).toString(), [brandColor]);
    const brandLight = useMemo(() => tinycolor(brandColor).lighten(15).toString(), [brandColor]);

    // Enrollment CTA palette mirrors the platform's `Button variant="secondary"`
    // (gray25 bg, gray900 text, gray50 hover) so the rewards button reads as
    // the same kind of action as "Order More" / "Call" on the order-complete
    // screen — consistent button language across every CTA on the page.
    const ctaPalette = { bg: '#F3F2F3', bgHover: '#E6E6E6', text: '#090404' };

    const ctaStyle = assignInlineVars({
      [ctaBgVar]: ctaPalette.bg,
      [ctaBgHoverVar]: ctaPalette.bgHover,
      [ctaTextVar]: ctaPalette.text,
    });

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
      const el = sceneRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      el.style.transform = `rotateY(${x * 15}deg) rotateX(${-y * 15}deg)`;
    }, []);

    const handleMouseLeave = useCallback(() => {
      if (sceneRef.current) sceneRef.current.style.transform = '';
    }, []);

    const hasReward = rewardsAvailable > 0;

    /* ── Title ── */
    const getTitleText = () => programName;

    /* ── Punch dots ── */
    const renderPunchDots = () => {
      const starSlots = earningThreshold - 1;

      if (earningThreshold <= 12) {
        const totalSlots = starSlots + 1; // stars + gift
        const columns = Math.min(totalSlots, 10);
        return (
          <div className={punchRow3d} style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
            {Array.from({ length: starSlots }, (_, i) => {
              const isFilled = hasReward || i < progress;
              const isProjected = false;
              return (
                <div
                  key={i}
                  className={clsx(
                    punchSlot3d,
                    isFilled ? punchFilled3d : isProjected ? punchProjected3d : punchEmpty3d,
                  )}
                  style={isFilled ? { backgroundColor: brandColor } : undefined}
                >
                  <Star
                    size={13}
                    color={
                      isFilled
                        ? '#fff'
                        : isProjected
                          ? 'rgba(255,255,255,0.5)'
                          : 'rgba(255,255,255,0.3)'
                    }
                    fill={isFilled ? '#fff' : 'none'}
                  />
                </div>
              );
            })}
            {(() => {
              // The gift slot is the visual representation of an
              // earned-and-redeemable reward, not of "almost-there"
              // progress. It activates ONLY when the server has actually
              // credited a reward (`rewardsAvailable > 0`). The previous
              // `progress >= starSlots` check lit it up one punch early —
              // e.g., on a 10-punch program with progress=9 the card would
              // read "1 away from a free item!" in copy while visually
              // showing the gift as already earned. Server-side `progress`
              // is always modulo-clamped to `0..threshold-1`, so the gift
              // is never active off the back of `progress` alone; we always
              // wait for the explicit reward credit.
              const giftActive = hasReward;
              return (
                <div className={clsx(punchSlot3d, giftActive ? giftSlotActive3d : giftSlot3d)}>
                  <Gift size={13} color={giftActive ? '#fff' : 'rgba(255,255,255,0.6)'} />
                </div>
              );
            })()}
          </div>
        );
      }

      return (
        <div className={compactRow3d}>
          <div className={compactTrack3d}>
            <div
              className={compactFill3d}
              style={{
                width: `${earningThreshold > 0 ? Math.min((progress / earningThreshold) * 100, 100) : 0}%`,
                background: 'rgba(255,255,255,0.8)',
              }}
            />
          </div>
          <span className={compactCounter3d}>
            {progress}/{earningThreshold}
          </span>
        </div>
      );
    };

    /* ── Points display ── */
    const renderPointsBody = () => (
      <div className={pointsArea3d}>
        <div className={clsx(pointsRow3d, enrollment && pointsRowEnroll)}>
          <span className={hasReward ? pointsNumReward3d : pointsNum3d}>
            {enrollment ? `+${pointsBalance.toLocaleString()}` : pointsBalance.toLocaleString()}
          </span>
          {!enrollment && (
            <span className={hasReward ? pointsUnitReward3d : pointsUnit3d}>pts</span>
          )}
          {enrollment?.pointsHint && (
            <span className={enrollPointsHint}>{enrollment.pointsHint}</span>
          )}
        </div>
      </div>
    );

    const renderBody = () => {
      if (isItemBased) return renderPunchDots();
      return renderPointsBody();
    };

    const isClickable = !!onClick;

    return (
      <div className={card3dWrapper}>
        <div className={card3dFloat}>
          <div
            ref={sceneRef}
            className={card3dScene}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={isClickable ? onClick : undefined}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onKeyDown={
              isClickable
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onClick?.();
                    }
                  }
                : undefined
            }
          >
            {/* 3D shadow layers */}
            <div className={card3dShadowDeep} style={{ background: brandDarker }} />
            <div className={card3dShadowMid} style={{ background: brandDark }} />

            {/* Card body */}
            <div
              className={clsx(
                card3dBody,
                isClickable && card3dClickable,
                enrollment && card3dBodyEnroll,
              )}
              style={{
                background: `linear-gradient(135deg, ${brandColor} 0%, ${brandMid} 50%, ${brandDark} 100%)`,
                boxShadow: `0 25px 50px rgba(0,0,0,0.18), 0 10px 20px rgba(0,0,0,0.12)`,
              }}
            >
              {/* Shine circles */}
              <div className={shineCircle1} />
              <div className={shineCircle2} />

              {/* Program name */}
              <span className={clsx(card3dTitle, enrollment && card3dTitleEnroll)}>
                {getTitleText()}
              </span>

              {/* Enrollment subtitle */}
              {enrollment && <span className={enrollSubtitle}>{enrollment.subtitle}</span>}

              {/* Reward ready label */}
              {!enrollment && rewardLabel && <span className={enrollSubtitle}>{rewardLabel}</span>}

              {/* Progress hint for item-based */}
              {!enrollment &&
                !rewardLabel &&
                isItemBased &&
                !hasReward &&
                earningThreshold - progress > 0 && (
                  <span className={enrollSubtitle}>
                    {earningThreshold - progress} away from a free item!
                  </span>
                )}

              {/* Spacer to push dots to bottom */}
              <div style={{ flex: 1 }} />
              {/* Body: stars or points */}
              {renderBody()}

              {/* Enrollment CTA */}
              {enrollment && (
                <button
                  className={enrollCta}
                  onClick={enrollment.onEnroll}
                  type="button"
                  style={ctaStyle}
                >
                  <span>{enrollment.ctaLabel}</span>
                  <span className={enrollCtaIcon} aria-hidden="true">
                    <ArrowRight size={16} strokeWidth={2.5} />
                  </span>
                </button>
              )}
            </div>

            {/* Sparkle decorations */}
            <Sparkles
              size={13}
              color={brandLight}
              className={sparkleIcon}
              style={{ top: 4, right: 4, animationDelay: '0s' }}
            />
            <Sparkle
              size={10}
              color="#FFD700"
              className={sparkleIcon}
              style={{ bottom: -2, right: 0, animationDelay: '1s' }}
            />
            <Sparkle
              size={8}
              color={brandLight}
              className={sparkleIcon}
              style={{ top: 30, left: -2, animationDelay: '2s' }}
            />

            {/* Reflection */}
            <div className={reflectionStrip} />
          </div>
        </div>
      </div>
    );
  },
);
